import React, { useMemo } from "react"

import { useAgentsById } from "@/agents/catalog"
import { useMetricsList } from "@/metrics/catalog"
import {
  Button,
  Input,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { Plus, X } from "lucide-react"

import {
  AUTO_ADD_MAX_FILTERS,
  AUTO_ADD_METRIC_OPERATORS,
  AUTO_ADD_STRING_METRIC_OPERATORS,
  type AutoAddMetadataFilter,
  type AutoAddMetricValueFilter,
} from "../../utils/autoAddRuleForm"

const METADATA_MATCH_TYPES: AutoAddMetadataFilter["match_type"][] = [
  "EQUALS",
  "CONTAINS",
  "ANY_OF",
]

const FLOAT_OUTPUT_TYPES = new Set(["float", "number", "numeric", "integer"])

function patchAt<T>(items: T[], index: number, patch: Partial<T>): T[] {
  return items.map((item, i) => (i === index ? { ...item, ...patch } : item))
}

interface MetadataFilterRowsProps {
  filters: AutoAddMetadataFilter[]
  onChange: (filters: AutoAddMetadataFilter[]) => void
}

export const MetadataFilterRows: React.FC<MetadataFilterRowsProps> = ({
  filters,
  onChange,
}) => {
  const update = (index: number, patch: Partial<AutoAddMetadataFilter>) =>
    onChange(patchAt(filters, index, patch))
  return (
    <div className="flex flex-col gap-2">
      {filters.map((filter, index) => (
        <div
          key={index}
          className="flex items-center gap-2"
          data-testid="auto-add-metadata-filter-row"
        >
          <Input
            aria-label="Metadata key"
            placeholder="key"
            value={filter.key}
            onChange={(event) => update(index, { key: event.target.value })}
          />
          <Select
            value={filter.match_type}
            onValueChange={(value) =>
              update(index, {
                match_type: value as AutoAddMetadataFilter["match_type"],
                values: value === "ANY_OF" ? [filter.value] : [],
              })
            }
          >
            <SelectTrigger aria-label="Metadata match type" className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {METADATA_MATCH_TYPES.map((matchType) => (
                <SelectItem key={matchType} value={matchType}>
                  {matchType === "EQUALS"
                    ? "equals"
                    : matchType === "CONTAINS"
                      ? "contains"
                      : "any of"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filter.match_type === "ANY_OF" ? (
            <div className="flex flex-1 flex-col gap-2">
              {(filter.values ?? []).map((value, valueIndex) => (
                <div key={valueIndex} className="flex items-center gap-2">
                  <Input
                    aria-label={`Metadata alternative ${valueIndex + 1}`}
                    value={value}
                    onChange={(event) =>
                      update(index, {
                        values: (filter.values ?? []).map((alternative, i) =>
                          i === valueIndex ? event.target.value : alternative,
                        ),
                      })
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    icon
                    tooltip={`Remove metadata alternative ${valueIndex + 1}`}
                    onClick={() =>
                      update(index, {
                        values: filter.values?.filter(
                          (_, i) => i !== valueIndex,
                        ),
                      })
                    }
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="secondary"
                disabled={(filter.values?.length ?? 0) >= AUTO_ADD_MAX_FILTERS}
                onClick={() =>
                  update(index, { values: [...(filter.values ?? []), ""] })
                }
              >
                Add alternative
              </Button>
            </div>
          ) : (
            <Input
              aria-label="Metadata value"
              placeholder="value"
              value={filter.value}
              onChange={(event) => update(index, { value: event.target.value })}
            />
          )}
          <Button
            type="button"
            variant="ghost"
            icon
            tooltip="Remove metadata filter"
            onClick={() => onChange(filters.filter((_, i) => i !== index))}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="secondary"
        className="self-start"
        disabled={filters.length >= AUTO_ADD_MAX_FILTERS}
        onClick={() =>
          onChange([...filters, { key: "", value: "", match_type: "EQUALS" }])
        }
      >
        <Plus className="h-4 w-4" />
        Add metadata filter
      </Button>
    </div>
  )
}

interface MetricValueFilterRowsProps {
  filters: AutoAddMetricValueFilter[]
  onChange: (filters: AutoAddMetricValueFilter[]) => void
}

export const MetricValueFilterRows: React.FC<MetricValueFilterRowsProps> = ({
  filters,
  onChange,
}) => {
  const metricsQuery = useMetricsList()
  const metricOptions = useMemo(
    () =>
      Object.values(metricsQuery.data ?? {})
        .flatMap((metric) => (metric.id ? [{ ...metric, id: metric.id }] : []))
        .map((metric) => ({
          id: metric.id,
          label: metric.metric_display_name,
          valueType: FLOAT_OUTPUT_TYPES.has(
            (metric.output_type ?? "").toLowerCase(),
          )
            ? ("float" as const)
            : ("string" as const),
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [metricsQuery.data],
  )
  const update = (index: number, patch: Partial<AutoAddMetricValueFilter>) =>
    onChange(patchAt(filters, index, patch))

  return (
    <div className="flex flex-col gap-2">
      {filters.map((filter, index) => {
        const operators =
          filter.value_type === "string"
            ? AUTO_ADD_STRING_METRIC_OPERATORS
            : AUTO_ADD_METRIC_OPERATORS
        return (
          <div
            key={index}
            className="flex items-center gap-2"
            data-testid="auto-add-metric-filter-row"
          >
            <Select
              value={filter.metric_id}
              onValueChange={(metricId) => {
                const option = metricOptions.find((o) => o.id === metricId)
                const valueType = option?.valueType ?? "string"
                const operatorAllowed =
                  valueType === "float" ||
                  (
                    AUTO_ADD_STRING_METRIC_OPERATORS as readonly string[]
                  ).includes(filter.operator)
                update(index, {
                  metric_id: metricId,
                  value_type: valueType,
                  operator: operatorAllowed ? filter.operator : "=",
                })
              }}
            >
              <SelectTrigger aria-label="Metric" className="min-w-40">
                <SelectValue
                  placeholder={metricsQuery.isLoading ? "Loading..." : "Metric"}
                />
              </SelectTrigger>
              <SelectContent>
                {metricOptions.map((option) => (
                  <SelectItem key={option.id} value={option.id}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filter.operator}
              onValueChange={(operator) =>
                update(index, {
                  operator: operator as AutoAddMetricValueFilter["operator"],
                })
              }
            >
              <SelectTrigger aria-label="Metric operator" className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {operators.map((operator) => (
                  <SelectItem key={operator} value={operator}>
                    {operator}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filter.operator !== "exists" && (
              <Input
                aria-label="Metric value"
                placeholder={filter.value_type === "float" ? "0.5" : "value"}
                value={filter.value}
                onChange={(event) =>
                  update(index, { value: event.target.value })
                }
              />
            )}
            {filter.operator === "range" && (
              <Input
                aria-label="Metric range upper bound"
                placeholder="max"
                value={filter.range_max ?? ""}
                onChange={(event) =>
                  update(index, { range_max: event.target.value })
                }
              />
            )}
            <Button
              type="button"
              variant="ghost"
              icon
              tooltip="Remove metric filter"
              onClick={() => onChange(filters.filter((_, i) => i !== index))}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )
      })}
      <Button
        type="button"
        variant="secondary"
        className="self-start"
        disabled={filters.length >= AUTO_ADD_MAX_FILTERS}
        onClick={() =>
          onChange([
            ...filters,
            { metric_id: "", operator: "=", value: "", value_type: "string" },
          ])
        }
      >
        <Plus className="h-4 w-4" />
        Add metric filter
      </Button>
    </div>
  )
}

interface AgentFilterSelectProps {
  agentIds: string[]
  onChange: (agentIds: string[]) => void
}

export const AgentFilterSelect: React.FC<AgentFilterSelectProps> = ({
  agentIds,
  onChange,
}) => {
  const { agentsById, isLoading } = useAgentsById()
  const options = useMemo(
    () =>
      Object.values(agentsById)
        .filter((agent) => (agent as { active?: boolean }).active !== false)
        .map((agent) => ({ value: agent.id, label: agent.display_name }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [agentsById],
  )
  return (
    <MultiSelect
      aria-label="Agents"
      options={options}
      value={agentIds}
      onValueChange={onChange}
      searchable
      disabled={isLoading}
      placeholder={isLoading ? "Loading agents..." : "Any agent"}
    />
  )
}
